import type {
  AdminGetAssistantSessionDetailQuery,
  AdminListAssistantFeedbackForThreadQuery,
} from '../__generated__/AssistantSessionsScene.gql';

export type MessageRow = AdminGetAssistantSessionDetailQuery['adminGetAssistantSessionDetail']['messages'][number];
export type FeedbackRow = AdminListAssistantFeedbackForThreadQuery['adminListAssistantFeedbackForThread'][number];

export interface IRoleAccent {
  border: string;
  label: string;
  /**
   * Color for the row's `headerPreview` slot — the inline body excerpt
   * (user/assistant prose, tool-call params, system kind). Three tiers
   * by information value: user + assistant get `fg-primary` (brightest —
   * what the operator is actually trying to read); toolResult /
   * system_* get `fg-tertiary` (dim — supporting metadata). The role
   * label itself keeps its accent color regardless, providing
   * row-type recognition independent of preview brightness.
   */
  prose: string;
}

export const ROLE_ACCENT: Record<string, IRoleAccent> = {
  user: { border: 'border-fg-brand-primary', label: 'text-fg-brand-primary', prose: 'text-fg-primary' },
  assistant: { border: 'border-fg-success-primary', label: 'text-fg-success-primary', prose: 'text-fg-primary' },
  toolResult: { border: 'border-fg-warning-primary', label: 'text-fg-warning-primary', prose: 'text-fg-tertiary' },
  system_instruction: { border: 'border-border-primary', label: 'text-fg-tertiary', prose: 'text-fg-tertiary' },
  system_observation: { border: 'border-border-primary', label: 'text-fg-tertiary', prose: 'text-fg-tertiary' },
};

export const DEFAULT_ROLE_ACCENT: IRoleAccent = {
  border: 'border-border-primary',
  label: 'text-fg-secondary',
  prose: 'text-fg-tertiary',
};

/**
 * Top-level payload keys whose array value renders collapsed by default in the
 * expanded JSON. `content` on a `toolResult` is `[{ type: 'text', text:
 * JSON.stringify(details) }]` — a stringified duplicate of the sibling
 * `details` object (see `jsonResult` in @orn/api), so it's noise an operator
 * almost never wants un-collapsed.
 */
export const COLLAPSED_TOOL_RESULT_KEYS: ReadonlySet<string> = new Set(['content']);

/**
 * Default prose treatment in the rendered JSON: `content` / `text` (the
 * Anthropic SDK message shape's prose keys) render bold bright-white. Cards
 * that want a role-specific color (e.g. user/assistant prose in yellow) pass
 * their own `stringKeyClasses` instead of this.
 */
export const PROMINENT_CONTENT_COLOR_CLASSES: ReadonlyMap<string, string> = new Map([
  ['content', 'font-semibold text-fg-primary'],
  ['text', 'font-semibold text-fg-primary'],
]);

/**
 * Message-payload string keys whose value is an opaque, unbreakable blob
 * (no whitespace for `pre-wrap` to break on) — truncated to a short preview
 * so it can't overflow the page width. `thinkingSignature` is the Anthropic
 * extended-thinking integrity token: never human-read, always huge.
 */
export const ELLIPSIZED_STRING_KEYS: ReadonlySet<string> = new Set(['thinkingSignature']);

/** User/assistant prose (`content` string + `text` blocks) renders bold yellow to foreground the conversation in the JSON. */
export const PROSE_COLOR_CLASSES: ReadonlyMap<string, string> = new Map([
  ['content', 'font-bold text-yellow-300'],
  ['text', 'font-bold text-yellow-300'],
]);

/**
 * Model-supplied audit params injected into every tool call's arguments
 * (`goal` is a user-facing status fragment, `justification` an internal
 * audit reason). Kept out of the toolResult header summary and surfaced
 * instead in the expanded `toolCall` section. Mirrors `AUDIT_PARAM_KEYS`
 * in @orn/api — duplicated here because the admin surface cannot import
 * from @orn/api.
 */
export const AUDIT_PARAM_KEYS: ReadonlySet<string> = new Set(['goal', 'justification']);

/** Per-key value colors for the expanded toolCall JSON: goal green, justification magenta. */
export const AUDIT_PARAM_COLOR_CLASSES: ReadonlyMap<string, string> = new Map([
  ['goal', 'text-fg-success-primary'],
  ['justification', 'text-fuchsia-400'],
]);

export interface ITurnGroup {
  turnId: string;
  messages: MessageRow[];
  /** ms since epoch of the earliest `startedAt` in the group, or null if every row lacks timing. */
  startedAtMs: number | null;
  /** ms since epoch of the latest `completedAt` in the group, or null if every row lacks timing. */
  completedAtMs: number | null;
  durationMs: number | null;
  roundCount: number;
  toolCallCount: number;
}

/**
 * Partition the loaded messages into ordered turn groups. Each turn's
 * rows are already contiguous in `sequence ASC` (the controller's append
 * contract holds them together), so a single linear pass suffices —
 * boundaries are wherever the `turnId` changes from the previous row.
 *
 * Aggregates (`startedAtMs` / `completedAtMs` / `roundCount` /
 * `toolCallCount`) are computed in the same pass so the header can
 * render them without re-walking the group. Rows that pre-date the
 * timing columns (`startedAt`/`completedAt` NULL) contribute to the
 * row count but not to the duration extent.
 */
export const groupMessagesByTurn = (messages: readonly MessageRow[]): ITurnGroup[] => {
  const groups: ITurnGroup[] = [];
  let current: ITurnGroup | null = null;
  for (const m of messages) {
    if (current === null || current.turnId !== m.turnId) {
      current = {
        turnId: m.turnId,
        messages: [],
        startedAtMs: null,
        completedAtMs: null,
        durationMs: null,
        roundCount: 0,
        toolCallCount: 0,
      };
      groups.push(current);
    }
    current.messages.push(m);
    if (m.role === 'assistant') {
      current.roundCount += 1;
    }
    if (m.role === 'toolResult') {
      current.toolCallCount += 1;
    }
    if (m.startedAt) {
      const ms = new Date(m.startedAt).getTime();
      if (current.startedAtMs === null || ms < current.startedAtMs) {
        current.startedAtMs = ms;
      }
    }
    if (m.completedAt) {
      const ms = new Date(m.completedAt).getTime();
      if (current.completedAtMs === null || ms > current.completedAtMs) {
        current.completedAtMs = ms;
      }
    }
  }
  for (const g of groups) {
    if (g.startedAtMs !== null && g.completedAtMs !== null) {
      g.durationMs = g.completedAtMs - g.startedAtMs;
    }
  }
  return groups;
};

/**
 * Pretty-print a millisecond duration for the turn header / latency
 * chips. `< 1s` shows as `123ms`; `≥ 1s` shows as `1.23s` so the
 * operator can scan turn lengths at a glance. NULL → `—`. Compact
 * (no space before unit) so per-row chips align cleanly in
 * fixed-width column cells.
 */
export const formatDurationMs = (ms: number | null): string => {
  if (ms === null) {
    return '—';
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
};

/**
 * One renderable chunk inside a turn group. Single `MessageCard`s
 * land as `{ kind: 'single' }`. Contiguous `toolResult` runs of 2+
 * land as `{ kind: 'parallel' }` — by the Anthropic API contract,
 * such a run always pairs to ONE assistant message with N tool_use
 * blocks, and pi-agent-core's `toolExecution: 'parallel'` default
 * means those N tools dispatch concurrently. Single-tool rounds
 * (run length 1) stay as `'single'` since there's nothing to bracket.
 */
export type TurnSegment =
  | { kind: 'single'; message: MessageRow }
  | {
      kind: 'parallel';
      messages: MessageRow[];
      /** ms since epoch — earliest `startedAt` across the parallel siblings (≈ when the assistant dispatched them). */
      startedAtMs: number | null;
      /** Max of each sibling's `latencyMs` — the slowest tool gates when the next round can start, so this is the wall-clock duration of the parallel block. */
      maxLatencyMs: number | null;
    };

export const buildTurnSegments = (messages: readonly MessageRow[]): TurnSegment[] => {
  const segments: TurnSegment[] = [];
  let i = 0;
  while (i < messages.length) {
    if (messages[i].role === 'toolResult') {
      const start = i;
      while (i < messages.length && messages[i].role === 'toolResult') {
        i += 1;
      }
      const slice = messages.slice(start, i);
      if (slice.length >= 2) {
        let startedAtMs: number | null = null;
        let maxLatencyMs: number | null = null;
        for (const m of slice) {
          if (m.startedAt) {
            const ms = new Date(m.startedAt).getTime();
            if (startedAtMs === null || ms < startedAtMs) {
              startedAtMs = ms;
            }
          }
          if (m.latencyMs !== null && m.latencyMs !== undefined) {
            if (maxLatencyMs === null || m.latencyMs > maxLatencyMs) {
              maxLatencyMs = m.latencyMs;
            }
          }
        }
        segments.push({ kind: 'parallel', messages: slice, startedAtMs, maxLatencyMs });
      } else {
        segments.push({ kind: 'single', message: slice[0] });
      }
    } else {
      segments.push({ kind: 'single', message: messages[i] });
      i += 1;
    }
  }
  return segments;
};

/**
 * Format the row's `startedAt` as a Δ-from-turn-start offset (e.g.
 * `+1.23s`). The first row of each turn shows `+0.00 s`. Falls back to
 * `—` when either the row or the turn lacks a `startedAt` — typically
 * legacy rows that pre-date the timing columns.
 */
export const formatTurnDelta = (rowStartedAt: Date | string | null, turnStartedAtMs: number | null): string => {
  if (rowStartedAt === null || turnStartedAtMs === null) {
    return '—';
  }
  const deltaMs = new Date(rowStartedAt).getTime() - turnStartedAtMs;
  if (deltaMs < 0) {
    return '—';
  }
  if (deltaMs < 1000) {
    return `+${deltaMs}ms`;
  }
  return `+${(deltaMs / 1000).toFixed(2)}s`;
};

/**
 * Walk every assistant row in `messages` and index every `toolCall`
 * content block by its `id`, returning `Map<toolCallId, arguments>` so
 * a paired `toolResult` row's params can be looked up O(1). pi-agent-
 * core normalizes Anthropic's raw `tool_use` (`input`) and OpenAI's
 * `function_call` (`arguments`) into one persisted shape on
 * `AssistantMessage.content[]` — `{ type: 'toolCall', id, name,
 * arguments }` — and pairs the toolResult by `toolCallId` ↔
 * `toolCall.id`. Single pass per turn at render time, no extra fetch.
 */
export const buildToolCallArgsIndex = (messages: readonly MessageRow[]): Map<string, unknown> => {
  const index = new Map<string, unknown>();
  for (const m of messages) {
    if (m.role !== 'assistant' || m.payload === null || typeof m.payload !== 'object') {
      continue;
    }
    const content = (m.payload as Record<string, unknown>).content;
    if (!Array.isArray(content)) {
      continue;
    }
    for (const block of content) {
      if (block === null || typeof block !== 'object') {
        continue;
      }
      const b = block as Record<string, unknown>;
      if (b.type === 'toolCall' && typeof b.id === 'string') {
        index.set(b.id, b.arguments);
      }
    }
  }
  return index;
};

/**
 * Render a tool call's `input` object as `(key: value, key: value, ...)`
 * for the toolResult card's header preview. Values are rendered as a
 * short scalar form so the line stays scannable; the full structure is
 * still in the JSON body on expand. Keys in `excludeKeys` are dropped
 * from the summary (the audit params `goal` / `justification` are surfaced
 * separately in the expanded toolCall section, not inline). Returns `''`
 * for an empty / missing input — or one whose only keys are excluded — so
 * the caller can fall through to the no-preview case.
 */
export const formatToolParams = (input: unknown, excludeKeys?: ReadonlySet<string>): string => {
  if (input === null || typeof input !== 'object') {
    return '';
  }
  const entries = Object.entries(input as Record<string, unknown>).filter(([k]) => !(excludeKeys?.has(k) ?? false));
  if (entries.length === 0) {
    return '';
  }
  const parts = entries.map(([k, v]) => `${k}: ${formatParamValue(v)}`);
  return `(${parts.join(', ')})`;
};

const MAX_PARAM_STRING_LEN = 40;
const formatParamValue = (v: unknown): string => {
  if (v === null) {
    return 'null';
  }
  if (typeof v === 'string') {
    return v.length > MAX_PARAM_STRING_LEN ? `"${v.slice(0, MAX_PARAM_STRING_LEN)}…"` : `"${v}"`;
  }
  if (typeof v === 'number' || typeof v === 'boolean') {
    return String(v);
  }
  if (Array.isArray(v)) {
    return `[${v.length}]`;
  }
  if (typeof v === 'object') {
    return `{${Object.keys(v as Record<string, unknown>).length}}`;
  }
  return typeof v;
};

/**
 * Extract a `toolResult` row's `toolCallId` from its GraphQL JSON payload.
 * Defensive — a producer drift that drops the field shouldn't crash the
 * renderer.
 */
export const readToolCallId = (payload: unknown): string | null => {
  if (payload === null || typeof payload !== 'object') {
    return null;
  }
  const id = (payload as Record<string, unknown>).toolCallId;
  return typeof id === 'string' ? id : null;
};

/**
 * Narrow the `toolResult` payload shape from the `unknown` GraphQL JSON
 * scalar so the collapsed header can surface `toolName` + `isError`
 * without rendering the (often noisy) `details` body. Defensive on every
 * field — a future producer drift shouldn't crash the renderer.
 */
export const readToolResultPayload = (payload: unknown): { toolName: string | null; isError: boolean } => {
  if (payload === null || typeof payload !== 'object') {
    return { toolName: null, isError: false };
  }
  const obj = payload as Record<string, unknown>;
  const toolName = typeof obj.toolName === 'string' ? obj.toolName : null;
  const isError = obj.isError === true;
  return { toolName, isError };
};

/**
 * Pull the `kind` discriminator out of a `system_observation` or
 * `system_instruction` payload (`'attachment'`, `'session_bootstrap'`,
 * etc.). Both payload families use the same `kind` field and the enum
 * is intentionally open to grow — keep the read defensive so a newly
 * added kind that mops-web hasn't been rebuilt against still renders
 * cleanly.
 */
export const readPayloadKind = (payload: unknown): string | null => {
  if (payload === null || typeof payload !== 'object') {
    return null;
  }
  const kind = (payload as Record<string, unknown>).kind;
  return typeof kind === 'string' ? kind : null;
};

export interface IObservationDocument {
  id: string;
  name: string | null;
}

/**
 * Pull the attachment `documents` array out of a `system_observation`
 * payload of kind `attachment` — each entry is the `{ id, name }` of an
 * uploaded document the inspector turns into a download link. Defensive
 * on every field: a non-object payload, a missing/non-array `documents`,
 * or an entry without a string `id` is dropped rather than crashing the
 * renderer. A non-string `name` is coerced to `null`.
 */
export const readObservationDocuments = (payload: unknown): IObservationDocument[] => {
  if (payload === null || typeof payload !== 'object') {
    return [];
  }
  const documents = (payload as Record<string, unknown>).documents;
  if (!Array.isArray(documents)) {
    return [];
  }
  const out: IObservationDocument[] = [];
  for (const entry of documents) {
    if (entry !== null && typeof entry === 'object') {
      const e = entry as Record<string, unknown>;
      if (typeof e.id === 'string') {
        out.push({ id: e.id, name: typeof e.name === 'string' ? e.name : null });
      }
    }
  }
  return out;
};

/**
 * Pull every `{ type: 'text', text: string }` block out of a user or
 * assistant message's `content` array. The Anthropic SDK message shape
 * interleaves `text` and `tool_use` (or, on user rows, `tool_result`)
 * blocks; the prose is what an operator scanning a transcript actually
 * wants to read, so we lift it above the collapsed JSON. Defensive —
 * non-array content (legacy plain-string payloads, or future shape drift)
 * falls back gracefully.
 */
export const extractTextBlocks = (payload: unknown): string[] => {
  if (payload === null || typeof payload !== 'object') {
    return [];
  }
  const content = (payload as Record<string, unknown>).content;
  if (typeof content === 'string') {
    return content.trim().length > 0 ? [content] : [];
  }
  if (!Array.isArray(content)) {
    return [];
  }
  const out: string[] = [];
  for (const block of content) {
    if (block !== null && typeof block === 'object') {
      const b = block as Record<string, unknown>;
      if (b.type === 'text' && typeof b.text === 'string' && b.text.trim().length > 0) {
        out.push(b.text);
      }
    }
  }
  return out;
};
